import { apiClient } from '../../api/client';
import type { components } from '../../api/generated/schema';
import { toCollection } from '../collections/mapping';
import { mapped } from '../errors';
import { toCollectionRef, toPersonRef } from '../mapping';
import type { AudienceRepository } from './AudienceRepository';
import { toMember, toMembershipChecklist } from './mapping';

type S = components['schemas'];

const enc = encodeURIComponent;
const collection = (id: string) => `/api/v1/collections/${enc(id)}/`;
const unfollowPath = (personId: string) => `/api/v1/me/following/${enc(personId)}/unfollow/`;
const removeFollowerPath = (personId: string) => `/api/v1/me/followers/${enc(personId)}/remove/`;

export const httpAudienceRepository: AudienceRepository = {
  members: (collectionId: string) =>
    mapped(async () =>
      (await apiClient.get<S['Member'][]>(`${collection(collectionId)}members/`)).map(toMember),
    ),
  myFollowers: (myId: string) =>
    mapped(async () =>
      (await apiClient.get<S['PersonRef'][]>(`/api/v1/people/${enc(myId)}/followers/`)).map(
        toPersonRef,
      ),
    ),
  removeMember: (collectionId: string, personId: string) =>
    mapped(async () => {
      await apiClient.send('DELETE', `${collection(collectionId)}members/${enc(personId)}/`);
    }),
  leave: (collectionId: string) =>
    mapped(async () => {
      await apiClient.send('POST', `${collection(collectionId)}leave/`);
    }),
  mute: (collectionId: string) =>
    mapped(async () => {
      await apiClient.send('PUT', `${collection(collectionId)}mute/`);
    }),
  unmute: (collectionId: string) =>
    mapped(async () => {
      await apiClient.send('DELETE', `${collection(collectionId)}mute/`);
    }),
  myMutes: () =>
    mapped(async () =>
      (await apiClient.get<S['CollectionRef'][]>('/api/v1/me/mutes/')).map(toCollectionRef),
    ),
  makePrivatePreview: (collectionId: string) =>
    mapped(async () =>
      (await apiClient.get<S['PersonRef'][]>(`${collection(collectionId)}make-private/`)).map(
        toPersonRef,
      ),
    ),
  makePrivate: (collectionId: string, keepPersonIds: string[], shownPersonIds: string[]) =>
    mapped(async () =>
      toCollection(
        await apiClient.send<S['Collection']>('POST', `${collection(collectionId)}make-private/`, {
          keep_person_ids: keepPersonIds,
          confirm_person_ids: shownPersonIds,
        }),
      ),
    ),
  myFollowing: () =>
    mapped(async () =>
      (await apiClient.get<S['PersonRef'][]>('/api/v1/me/following/')).map(toPersonRef),
    ),
  unfollowPreview: (personId: string) =>
    mapped(async () =>
      toMembershipChecklist(await apiClient.get<S['MembershipChecklist']>(unfollowPath(personId))),
    ),
  unfollow: (personId: string, leaveCollectionIds: string[]) =>
    mapped(async () => {
      await apiClient.send('POST', unfollowPath(personId), {
        leave_collection_ids: leaveCollectionIds,
      });
    }),
  removeFollowerPreview: (personId: string) =>
    mapped(async () =>
      toMembershipChecklist(
        await apiClient.get<S['MembershipChecklist']>(removeFollowerPath(personId)),
      ),
    ),
  removeFollower: (personId: string, endCollectionIds: string[]) =>
    mapped(async () => {
      await apiClient.send('POST', removeFollowerPath(personId), {
        end_collection_ids: endCollectionIds,
      });
    }),
};
